<script lang="ts">
  import VirtualList from 'svelte-tiny-virtual-list';
  import InfiniteLoading from 'svelte-infinite-loading';
  import type { InfiniteEvent } from 'svelte-infinite-loading';
  import {
    type InfiniteLeaderboard,
    type Period,
    type SLeaderboardEntry,
  } from '~/types';
  import Entry from './Entry.svelte';
  import { onDestroy, onMount, tick } from 'svelte';
  import Spinner from '../components/Spinner.svelte';
  import {
    getLeaderboardQueryKey,
    loadLeaderboardQuery,
    loadLocalLeaderboard,
    type LeaderboardPages,
  } from './queries.svelte';
  import {
    useQueryClient,
    type CreateInfiniteQueryResult,
  } from '@tanstack/svelte-query';
  import { delay } from '~/animations/utils.svelte';
  import { getTabContext } from '~/components/tabs/Tabs.svelte';
  import JumpButton from './JumpButton.svelte';
  import ScrollShadow from '~/components/ScrollShadow.svelte';

  type Props = {
    tab: { current: Period };
    listHeight: number;
    currentGameId: string;
    lost: boolean;
  };

  const props: Props = $props();

  let mounted = $state(false);
  let innerWidth = $state(0);

  const small = $derived(innerWidth <= 360);
  const itemHeight = $derived(small ? 120 : 150);

  const tabs = getTabContext();

  const key = $derived(props.lost ? props.currentGameId : 'progress');
  const leaderboard = $derived(loadLeaderboardQuery(props.tab.current, key));
  const getEntries = (
    leaderboard: CreateInfiniteQueryResult<LeaderboardPages>,
  ): SLeaderboardEntry[] => leaderboard?.data?.pages.flat() ?? [];
  const entries = $derived(getEntries(leaderboard));
  const itemCount = $derived(entries.length);
  // initially instant for mounting purposes
  let scrollToBehaviour: ScrollBehavior = $state('instant');
  let scrollOffset = $derived(tabs.scrollPosition[props.tab.current] ?? 0);
  let scrollToIndex = $state(-1);

  const queryClient = useQueryClient();
  onMount(async () => {
    if (props.tab.current === 'local') {
      // local entries
      queryClient.setQueryData<InfiniteLeaderboard>(
        getLeaderboardQueryKey(props.tab.current, key),
        {
          pageParams: [
            { id: '', direction: 'desc', score: '0', period: 'local', rank: 0 },
          ],
          pages: [loadLocalLeaderboard()],
        },
      );
    }
    // if unloaded
    if (entries.length <= 1) {
      if (key !== 'progress') {
        await leaderboard.fetchPreviousPage();
      }
      await leaderboard.fetchNextPage();
    }
    mounted = true;
    await tick();
    scrollToIndex = getEntries(leaderboard).findIndex(
      (e) => e.id === props.currentGameId,
    );
  });

  onDestroy(() => {
    mounted = false;
  });

  let scrollTimer: NodeJS.Timeout;
  const handleScroll = (e: { offset: number }) => {
    // TODO
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      tabs.scrollPosition[props.tab.current] = e.offset;
      scrollToIndex = -1;
    }, 100);
  };

  const handleScrollToIndex = (idx: number) => {
    scrollToBehaviour = 'smooth';
    scrollToIndex = idx;
  };

  // react to leaderboard changes
  const handleInfinite = $derived(
    async (e: InfiniteEvent, direction: 'asc' | 'desc') => {
      if (!mounted) return;
      // if local
      if (props.tab.current === 'local') {
        e.detail.complete();
        return;
      }
      // if loading from top
      if (!props.lost && direction === 'asc') {
        e.detail.complete();
        return;
      }
      if (direction === 'asc' && !leaderboard.isFetchingPreviousPage) {
        if (!leaderboard.hasPreviousPage) {
          e.detail.complete();
          return;
        }
        const res = await leaderboard.fetchPreviousPage();
        if (res.hasPreviousPage) {
          e.detail.loaded();
        } else {
          e.detail.complete();
        }
      } else if (direction === 'desc' && !leaderboard.isFetchingNextPage) {
        if (!leaderboard.hasNextPage) {
          e.detail.complete();
          return;
        }
        const res = await leaderboard.fetchNextPage();
        if (res.hasNextPage) {
          e.detail.loaded();
        } else {
          e.detail.complete();
        }
      } else {
        // weird debouncing
        while (
          leaderboard.isFetchingPreviousPage ||
          leaderboard.isFetchingNextPage
        ) {
          await delay(1);
        }
        e.detail.loaded();
      }
    },
  );

  const currentScoreIdx = $derived(
    getEntries(leaderboard).findIndex((e) => e.id === props.currentGameId),
  );
</script>

<svelte:window bind:innerWidth />
{#if mounted}
  {#if props.tab.current === 'local' && entries.length === 0}
    <div class="empty-local">
      <p>Play a few games to see your personal leaderboard here.</p>
    </div>
  {:else}
  <ScrollShadow>
    <VirtualList
      width="100%"
      height={props.listHeight}
      {itemCount}
      itemSize={itemHeight}
      overscanCount={5}
      {scrollOffset}
      scrollToIndex={scrollToIndex !== -1 ? scrollToIndex : undefined}
      scrollToAlignment="center"
      {scrollToBehaviour}
      getKey={(idx) => entries[idx].id}
      onAfterScroll={handleScroll}
    >
      {#snippet header()}
        <InfiniteLoading
          direction="top"
          distance={itemHeight}
          on:infinite={(e) => handleInfinite(e, 'asc')}
        >
          <div slot="noMore"></div>
          <div slot="noResults"></div>
          <Spinner slot="spinner" />
        </InfiniteLoading>
      {/snippet}
      {#snippet item({ index, style })}
        <div class:small {style}>
          <Entry
            tab={props.tab.current}
            entry={entries[index]}
            current={(entries[index].id ?? entries[index].gameId) === props.currentGameId}
            position={props.lost ? entries[index].rank : index + 1}
          />
        </div>
      {/snippet}
      {#snippet footer()}
        <InfiniteLoading
          direction="bottom"
          distance={itemHeight}
          on:infinite={(e) => handleInfinite(e, 'desc')}
        >
          <div slot="noMore">no more data</div>
          <div slot="noResults" class="no-results">you've reached the end</div>
          <Spinner slot="spinner" />
        </InfiniteLoading>
      {/snippet}
    </VirtualList>
  </ScrollShadow>
  {#if props.lost}
    <JumpButton
      scrollOffset={tabs.scrollPosition[props.tab.current]}
      listHeight={props.listHeight}
      {itemHeight}
      index={currentScoreIdx}
      onScrollToIndex={handleScrollToIndex}
    >
      back to you
    </JumpButton>
  {:else}
    <JumpButton
      scrollOffset={tabs.scrollPosition[props.tab.current]}
      listHeight={props.listHeight}
      {itemHeight}
      index={0}
      onScrollToIndex={handleScrollToIndex}
    >
      back to top
    </JumpButton>
  {/if}
  {/if}
{:else}
  <div class="spinner">
    <Spinner />
  </div>
{/if}

<style lang="scss">
  .spinner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .small {
    font-size: 0.75em;
  }
  .no-results {
    padding-bottom: 1em;
  }
  .empty-local {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2em;
    text-align: center;
    opacity: 0.6;
  }
</style>
